import React, { useState, useMemo, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNotice } from '../shared/NoticeProvider';
import { usePageHeader } from '../../../contexts/PageHeaderContext';
import {
  useFOSSyncStatus,
  useFOSSyncHealth,
  useFOSStats,
  useFOSBackfillStatus,
  useFOSFailedDecisions,
  useFOSStuckDecisions,
  useTriggerFOSSync,
  useReprocessDecisions,
  useReprocessSingleDecision,
  useResumeFOSDecision,
  useRequeueStuckDecisions,
  useStartBackfill,
  useFOSDecisions,
  useFOSDecisionDetail,
  useProcessEmbeddings,
  useFOSProjectionStatus,
  useComputeProjections,
  useRelabelClusters,
  type FOSDecisionQueryParams
} from '../../../hooks/useFOSAdmin';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../ui/collapsible';
import IconWrapper from '../../ui/IconWrapper';
import {
  FiDatabase,
  FiRefreshCw,
  FiPlay,
  FiDownload,
  FiChevronDown,
  FiChevronUp,
  FiZap,
  FiCheckCircle,
  FiAlertCircle,
  FiGrid,
  FiTag
} from 'react-icons/fi';
import FOSAdminBrowseTab from './FOSAdminBrowseTab';
import FOSAdminIssuesCard from './FOSAdminIssuesCard';
import FOSDecisionDetailDialog from './FOSDecisionDetailDialog';
import FOSProcessingLogFeed from './FOSProcessingLogFeed';
import FOSProjectionJobsPanel from './FOSProjectionJobsPanel';
import { useFOSProcessingLog } from '../../../hooks/useFOSProcessingLog';
import {
  useCreateProjectionJob,
  useProjectionJobProvider,
} from '../../../hooks/useFOSProjectionJobs';
import { formatRelativeTime, sectorOptions } from './fosAdminDashboardUtils';

const FOSAdminDashboard: React.FC = () => {
  const { setPageInfo } = usePageHeader();
  const notice = useNotice();
  const queryClient = useQueryClient();

  // Debounced refresh for stats - prevents excessive API calls during rapid processing
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const refreshStats = useCallback(() => {
    // Clear any pending refresh
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
    // Debounce: wait 500ms before refreshing to batch rapid events
    refreshTimeoutRef.current = setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'fos', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'fos', 'backfill', 'status'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'fos', 'decisions', 'failed'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'fos', 'decisions', 'stuck'] });
    }, 500);
  }, [queryClient]);

  // Subscribe to FOS processing events for real-time stats updates
  const { isProcessing: isRealtimeProcessing, entries: fosLogEntries } = useFOSProcessingLog({
    onEvent: refreshStats,
    maxEntries: 50,
  });

  // Tab state
  const [activeTab, setActiveTab] = useState('overview');

  // Progressive disclosure states
  const [showImportOptions, setShowImportOptions] = useState(false);
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);
  const [showVisualizationOptions, setShowVisualizationOptions] = useState(false);
  const [showJobsPanel, setShowJobsPanel] = useState(false);

  // Provider detection for K8s vs Celery
  const { isKubernetes, providerLabel } = useProjectionJobProvider();
  const createK8sJobMutation = useCreateProjectionJob();

  // Visualization parameters
  const [vizMinClusterSize, setVizMinClusterSize] = useState('100');
  const [vizNNeighbors, setVizNNeighbors] = useState('15');
  const [vizMinDist, setVizMinDist] = useState('0.1');

  // Import options state
  const [syncDateFrom, setSyncDateFrom] = useState('');
  const [syncDateTo, setSyncDateTo] = useState('');
  const [syncIndustrySector, setSyncIndustrySector] = useState('Insurance');
  const [syncMaxDecisions, setSyncMaxDecisions] = useState('');

  // Processing batch sizes
  const [processBatchSize, setProcessBatchSize] = useState('');
  const [embedBatchSize, setEmbedBatchSize] = useState('');

  // Browse decisions state
  const [browsePage, setBrowsePage] = useState(1);
  const browsePageSize = 25;
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [processingStatusFilter, setProcessingStatusFilter] = useState<string>('');
  const [decisionStatusFilter, setDecisionStatusFilter] = useState<string>('');
  const [industrySectorFilter, setIndustrySectorFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState('decision_date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Decision detail modal state
  const [selectedDecisionId, setSelectedDecisionId] = useState<string | null>(null);

  // Debounce search input
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setBrowsePage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Data hooks
  const { data: syncStatus, isLoading: syncStatusLoading } = useFOSSyncStatus();
  const { data: syncHealth } = useFOSSyncHealth();
  const { data: stats, isLoading: statsLoading } = useFOSStats();
  const { data: backfillStatus } = useFOSBackfillStatus();
  const { data: failedDecisions } = useFOSFailedDecisions(undefined, 5, 0);
  const { data: stuckDecisions } = useFOSStuckDecisions(24, 5, 0);
  const { data: projectionStatus, isLoading: projectionStatusLoading } = useFOSProjectionStatus();

  // Browse decisions query
  const browseQueryParams: FOSDecisionQueryParams = useMemo(() => ({
    processing_status: processingStatusFilter || undefined,
    decision_status: decisionStatusFilter || undefined,
    industry_sector: industrySectorFilter || undefined,
    search: debouncedSearch || undefined,
    sort_by: sortBy,
    sort_order: sortOrder,
    limit: browsePageSize,
    offset: (browsePage - 1) * browsePageSize,
  }), [processingStatusFilter, decisionStatusFilter, industrySectorFilter, debouncedSearch, sortBy, sortOrder, browsePage]);

  const { data: decisionsData, isLoading: decisionsLoading } = useFOSDecisions(browseQueryParams);
  const { data: decisionDetail, isLoading: detailLoading } = useFOSDecisionDetail(selectedDecisionId);

  // Mutation hooks
  const triggerSyncMutation = useTriggerFOSSync();
  const reprocessAllMutation = useReprocessDecisions();
  const reprocessSingleMutation = useReprocessSingleDecision();
  const resumeDecisionMutation = useResumeFOSDecision();
  const requeueStuckMutation = useRequeueStuckDecisions();
  const startBackfillMutation = useStartBackfill();
  const processEmbeddingsMutation = useProcessEmbeddings();
  const computeProjectionsMutation = useComputeProjections();
  const relabelClustersMutation = useRelabelClusters();

  // Set page header
  React.useEffect(() => {
    setPageInfo({
      title: 'FOS Decision Corpus',
      description: 'Import and process Financial Ombudsman Service decisions'
    });
  }, [setPageInfo]);

  // Computed values
  const totalDecisions = stats?.total ?? 0;
  const embeddedCount = stats?.embedded_count ?? 0;
  const pendingCount = backfillStatus?.pending ?? 0;
  const inProgressCount = backfillStatus?.in_progress ?? 0;
  const failedCount = backfillStatus?.failed ?? 0;
  const stuckCount = stuckDecisions?.total ?? 0;
  const issuesCount = failedCount + stuckCount;

  // Calculate summarized count (needs embedding) from the stats breakdown
  const summarizedCount = stats?.by_processing_state?.['summarized'] ?? 0;

  const isHealthy = syncHealth?.is_healthy && issuesCount === 0;
  const isProcessing = isRealtimeProcessing || inProgressCount > 0 || syncStatus?.status === 'syncing';
  const latestSyncSnapshot = useMemo(() => {
    for (const entry of fosLogEntries) {
      if (entry.level !== 'sync') continue;
      const { decisionsFound, decisionsImported, decisionsSkipped, stage, processed, total, percent } = entry.data;
      const hasCounts = decisionsFound !== undefined || decisionsImported !== undefined || decisionsSkipped !== undefined;
      if (!hasCounts) continue;
      return {
        decisionsFound: decisionsFound ?? 0,
        decisionsImported: decisionsImported ?? 0,
        decisionsSkipped: decisionsSkipped ?? 0,
        stage,
        processed,
        total,
        percent,
      };
    }
    return null;
  }, [fosLogEntries]);

  const syncDateError = Boolean(
    (syncDateTo && !syncDateFrom) ||
    (syncDateFrom && syncDateTo && syncDateFrom > syncDateTo)
  );

  // Handlers
  const handleImportDecisions = async () => {
    try {
      const maxDecisionsValue = syncMaxDecisions ? Number(syncMaxDecisions) : undefined;
      const maxDecisions = maxDecisionsValue && maxDecisionsValue > 0 ? maxDecisionsValue : undefined;
      const result = await triggerSyncMutation.mutateAsync({
        date_from: syncDateFrom || undefined,
        date_to: syncDateTo || undefined,
        max_decisions: maxDecisions,
        industry_sector: syncIndustrySector || undefined,
      });
      notice.show({ type: 'success', message: result.message || 'Import started successfully', autoDismissMs: 5000 });
    } catch (error) {
      console.error('Failed to import decisions:', error);
      notice.show({ type: 'error', message: 'Failed to start import', autoDismissMs: 5000 });
    }
  };

  const handleProcessPending = async () => {
    try {
      const batchSizeValue = parseInt(processBatchSize, 10);
      const batchSize = Number.isFinite(batchSizeValue) ? batchSizeValue : undefined;
      const result = await startBackfillMutation.mutateAsync(
        batchSize ? { batch_size: batchSize } : {}
      );
      notice.show({ type: 'success', message: result.message || 'Processing started successfully', autoDismissMs: 5000 });
    } catch (error) {
      console.error('Failed to process decisions:', error);
      notice.show({ type: 'error', message: 'Failed to start processing', autoDismissMs: 5000 });
    }
  };

  const handleProcessEmbeddings = async () => {
    try {
      const limitValue = parseInt(embedBatchSize, 10);
      const limit = Number.isFinite(limitValue) ? limitValue : undefined;
      const result = await processEmbeddingsMutation.mutateAsync(
        limit ? { limit } : {}
      );
      notice.show({ type: 'success', message: result.message || `${result.decisions_queued} decisions queued for embedding`, autoDismissMs: 5000 });
    } catch (error) {
      console.error('Failed to process embeddings:', error);
      notice.show({ type: 'error', message: 'Failed to start embedding processing', autoDismissMs: 5000 });
    }
  };

  const handleProcessSingle = async (decisionId: string) => {
    try {
      const result = await reprocessSingleMutation.mutateAsync(decisionId);
      notice.show({ type: 'success', message: result.message || 'Decision queued for processing', autoDismissMs: 5000 });
    } catch (error) {
      console.error('Failed to process decision:', error);
      notice.show({ type: 'error', message: 'Failed to queue decision for processing', autoDismissMs: 5000 });
    }
  };

  const handleReprocessAllFailed = async () => {
    try {
      const result = await reprocessAllMutation.mutateAsync({});
      notice.show({ type: 'success', message: result.message || `${result.decisions_queued} decisions queued for retry`, autoDismissMs: 5000 });
    } catch (error) {
      console.error('Failed to reprocess decisions:', error);
      notice.show({ type: 'error', message: 'Failed to retry failed decisions', autoDismissMs: 5000 });
    }
  };

  const handleResumeDecision = async (decisionId: string) => {
    try {
      const result = await resumeDecisionMutation.mutateAsync(decisionId);
      notice.show({ type: 'success', message: result.message || 'Decision resumed', autoDismissMs: 5000 });
    } catch (error) {
      console.error('Failed to resume decision:', error);
      notice.show({ type: 'error', message: 'Failed to resume decision', autoDismissMs: 5000 });
    }
  };

  const handleRequeueStuck = async () => {
    if (!stuckDecisions?.decisions?.length) return;
    try {
      const result = await requeueStuckMutation.mutateAsync({
        decision_ids: stuckDecisions.decisions.map((d) => d.id),
      });
      notice.show({ type: 'success', message: `${result.decisions_queued} stuck decisions requeued`, autoDismissMs: 5000 });
    } catch (error) {
      console.error('Failed to requeue stuck decisions:', error);
      notice.show({ type: 'error', message: 'Failed to requeue stuck decisions', autoDismissMs: 5000 });
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setDebouncedSearch('');
    setProcessingStatusFilter('');
    setDecisionStatusFilter('');
    setIndustrySectorFilter('');
    setBrowsePage(1);
  };

  const handleComputeProjections = async () => {
    const params = {
      min_cluster_size: parseInt(vizMinClusterSize) || 100,
      n_neighbors: parseInt(vizNNeighbors) || 15,
      min_dist: parseFloat(vizMinDist) || 0.1,
    };

    try {
      if (isKubernetes) {
        // Use K8s Job for staging/beta/production
        const result = await createK8sJobMutation.mutateAsync(params);
        notice.show({
          type: 'success',
          message: result.message || `Kubernetes Job ${result.job.name} created`,
          autoDismissMs: 5000
        });
        // Show jobs panel so user can see progress
        setShowJobsPanel(true);
      } else {
        // Use Celery for development
        const result = await computeProjectionsMutation.mutateAsync(params);
        notice.show({ type: 'success', message: result.message || 'Projection computation started', autoDismissMs: 5000 });
      }
    } catch (error) {
      console.error('Failed to compute projections:', error);
      notice.show({ type: 'error', message: 'Failed to start projection computation', autoDismissMs: 5000 });
    }
  };

  const handleRelabelClusters = async () => {
    try {
      const result = await relabelClustersMutation.mutateAsync();
      notice.show({ type: 'success', message: result.message || 'Cluster relabeling started', autoDismissMs: 5000 });
    } catch (error) {
      console.error('Failed to relabel clusters:', error);
      notice.show({ type: 'error', message: 'Failed to start cluster relabeling', autoDismissMs: 5000 });
    }
  };

  const hasActiveFilters = searchQuery || processingStatusFilter || decisionStatusFilter || industrySectorFilter;
  const totalBrowseItems = decisionsData?.total ?? 0;
  const totalBrowsePages = Math.max(1, Math.ceil(totalBrowseItems / browsePageSize));

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-8 py-6">
        <h1 className="text-2xl font-semibold text-gray-900">FOS Decision Corpus</h1>
        <p className="text-gray-600 mt-1">
          Import decisions from the Financial Ombudsman Service and process them for search
        </p>
      </div>

      {/* Main Content */}
      <div className="px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="browse">Browse Decisions</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* System Health Card */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {(syncStatusLoading || statsLoading) ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
                    ) : isHealthy ? (
                      <div className="p-2 bg-green-100 rounded-full">
                        <IconWrapper icon={FiCheckCircle} className="h-5 w-5 text-green-600" />
                      </div>
                    ) : isProcessing ? (
                      <div className="p-2 bg-blue-100 rounded-full">
                        <IconWrapper icon={FiRefreshCw} className="h-5 w-5 text-blue-600 animate-spin" />
                      </div>
                    ) : (
                      <div className="p-2 bg-yellow-100 rounded-full">
                        <IconWrapper icon={FiAlertCircle} className="h-5 w-5 text-yellow-600" />
                      </div>
                    )}
                    <div>
                      <CardTitle className="text-lg">System Status</CardTitle>
                      <CardDescription>
                        {isHealthy ? 'All systems operational' : isProcessing ? 'Processing in progress' : 'Needs attention'}
                      </CardDescription>
                    </div>
                  </div>
                  <Badge className={isHealthy ? 'bg-green-100 text-green-800' : isProcessing ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}>
                    {isHealthy ? 'Healthy' : isProcessing ? 'Processing' : 'Issues'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                {/* Key Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-gray-900">{totalDecisions.toLocaleString()}</p>
                    <p className="text-sm text-gray-500">Total</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{embeddedCount.toLocaleString()}</p>
                    <p className="text-sm text-gray-500">Ready for Search</p>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <p className="text-2xl font-bold text-purple-600">{summarizedCount.toLocaleString()}</p>
                    <p className="text-sm text-gray-500">Needs Embedding</p>
                  </div>
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{(pendingCount + inProgressCount).toLocaleString()}</p>
                    <p className="text-sm text-gray-500">Pending</p>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-lg">
                    <p className="text-2xl font-bold text-red-600">{issuesCount.toLocaleString()}</p>
                    <p className="text-sm text-gray-500">Issues</p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500">
                  <span>Last import: {formatRelativeTime(syncStatus?.last_sync_at)}</span>
                  {latestSyncSnapshot && (
                    <Badge
                      variant="outline"
                      className="bg-slate-50 border-slate-200 text-slate-700"
                      title={latestSyncSnapshot.stage || 'Latest sync snapshot'}
                    >
                      <IconWrapper icon={FiRefreshCw} className="h-3 w-3" />
                      Found {latestSyncSnapshot.decisionsFound.toLocaleString()} • Imported {latestSyncSnapshot.decisionsImported.toLocaleString()} • Skipped {latestSyncSnapshot.decisionsSkipped.toLocaleString()}
                    </Badge>
                  )}
                </div>

                {/* Technical Details (Progressive Disclosure) */}
                <Collapsible open={showTechnicalDetails} onOpenChange={setShowTechnicalDetails}>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm" className="mt-2 text-gray-500">
                      <IconWrapper icon={showTechnicalDetails ? FiChevronUp : FiChevronDown} className="h-4 w-4 mr-1" />
                      {showTechnicalDetails ? 'Hide' : 'Show'} technical details
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-3 pt-3 border-t border-gray-200">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Import Status</p>
                        <p className="font-medium">{syncStatus?.status ?? 'idle'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Processing Rate (24h)</p>
                        <p className="font-medium">{syncHealth?.processing_rate?.toLocaleString() ?? '-'}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Backlog</p>
                        <p className={`font-medium ${syncHealth?.backlog_warning ? 'text-red-600' : ''}`}>
                          {syncHealth?.backlog_count?.toLocaleString() ?? '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500">Errors (24h)</p>
                        <p className="font-medium">{syncHealth?.error_count_24h ?? '-'}</p>
                      </div>
                    </div>
                    {syncHealth?.health_issues?.length ? (
                      <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
                        <p className="text-xs font-medium text-amber-800">Health Issues:</p>
                        <ul className="mt-1 text-xs text-amber-700 list-disc list-inside">
                          {syncHealth.health_issues.map((issue) => (
                            <li key={issue}>{issue}</li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </CollapsibleContent>
                </Collapsible>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Import Decisions Card */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-full">
                      <IconWrapper icon={FiDownload} className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle className="text-base">Import Decisions</CardTitle>
                      <CardDescription className="text-xs">
                        Fetch new decisions from the FOS website
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Collapsible open={showImportOptions} onOpenChange={setShowImportOptions}>
                    <div className="flex gap-2">
                      <Button
                        onClick={handleImportDecisions}
                        disabled={triggerSyncMutation.isPending || syncStatus?.status === 'syncing'}
                        className="flex-1"
                      >
                        <IconWrapper icon={FiDownload} className={`h-4 w-4 mr-2 ${triggerSyncMutation.isPending ? 'animate-spin' : ''}`} />
                        {triggerSyncMutation.isPending ? 'Importing...' : 'Import Now'}
                      </Button>
                      <CollapsibleTrigger asChild>
                        <Button variant="outline" size="icon">
                          <IconWrapper icon={showImportOptions ? FiChevronUp : FiChevronDown} className="h-4 w-4" />
                        </Button>
                      </CollapsibleTrigger>
                    </div>
                    <CollapsibleContent className="mt-4 space-y-3">
                      <p className="text-xs text-gray-500">
                        By default, imports decisions since the last successful import. Use options below to customize.
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-gray-500">From Date</label>
                          <Input
                            type="date"
                            value={syncDateFrom}
                            onChange={(e) => setSyncDateFrom(e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">To Date</label>
                          <Input
                            type="date"
                            value={syncDateTo}
                            onChange={(e) => setSyncDateTo(e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Sector</label>
                          <Select value={syncIndustrySector} onValueChange={setSyncIndustrySector}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {sectorOptions.map((sector) => (
                                <SelectItem key={sector} value={sector}>{sector}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Max Decisions</label>
                          <Input
                            type="number"
                            min="1"
                            placeholder="No limit"
                            value={syncMaxDecisions}
                            onChange={(e) => setSyncMaxDecisions(e.target.value)}
                          />
                        </div>
                      </div>
                      {syncDateError && (
                        <p className="text-xs text-red-600">
                          Please provide a start date before the end date.
                        </p>
                      )}
                    </CollapsibleContent>
                  </Collapsible>
                </CardContent>
              </Card>

              {/* Process Decisions Card */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-full">
                      <IconWrapper icon={FiZap} className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <CardTitle className="text-base">Process Decisions</CardTitle>
                      <CardDescription className="text-xs">
                        Generate AI summaries and search embeddings
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Pending Processing */}
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Button
                        onClick={handleProcessPending}
                        disabled={startBackfillMutation.isPending || pendingCount === 0}
                        className="w-full"
                        variant={pendingCount > 0 ? 'default' : 'outline'}
                      >
                        <IconWrapper icon={FiPlay} className={`h-4 w-4 mr-2 ${startBackfillMutation.isPending ? 'animate-spin' : ''}`} />
                        {startBackfillMutation.isPending
                          ? 'Processing...'
                          : pendingCount > 0
                            ? `Process ${pendingCount.toLocaleString()} Pending`
                            : 'No Pending'
                        }
                      </Button>
                    </div>
                    <Input
                      type="number"
                      min="1"
                      value={processBatchSize}
                      onChange={(e) => setProcessBatchSize(e.target.value)}
                      className="w-24 text-center"
                      title="Batch size"
                      placeholder="All"
                    />
                  </div>

                  {/* Embedding Processing */}
                  {summarizedCount > 0 && (
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <Button
                          onClick={handleProcessEmbeddings}
                          disabled={processEmbeddingsMutation.isPending}
                          className="w-full"
                          variant="outline"
                        >
                          <IconWrapper icon={FiDatabase} className={`h-4 w-4 mr-2 ${processEmbeddingsMutation.isPending ? 'animate-spin' : ''}`} />
                          {processEmbeddingsMutation.isPending
                            ? 'Embedding...'
                            : `Embed ${summarizedCount.toLocaleString()} Summarized`
                          }
                        </Button>
                      </div>
                      <Input
                        type="number"
                        min="1"
                        value={embedBatchSize}
                        onChange={(e) => setEmbedBatchSize(e.target.value)}
                        className="w-24 text-center"
                        title="Batch size"
                        placeholder="All"
                      />
                    </div>
                  )}

                  <p className="text-xs text-gray-500">
                    Downloads full text, generates AI summaries, and creates embeddings for semantic search.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Visualization Management Card */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-100 rounded-full">
                    <IconWrapper icon={FiGrid} className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-base">Visualization</CardTitle>
                    <CardDescription className="text-xs">
                      UMAP projections and clusters for the FOS Explorer
                    </CardDescription>
                  </div>
                  {projectionStatusLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600" />
                  ) : projectionStatus ? (
                    <Badge className="bg-green-100 text-green-800">
                      {projectionStatus.total_clusters} clusters
                    </Badge>
                  ) : (
                    <Badge className="bg-gray-100 text-gray-600">Not computed</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {/* Projection Status */}
                {projectionStatus && (
                  <div className="grid grid-cols-3 gap-4 mb-4 text-center">
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <p className="text-lg font-semibold text-gray-900">{projectionStatus.total_points.toLocaleString()}</p>
                      <p className="text-xs text-gray-500">Points</p>
                    </div>
                    <div className="p-2 bg-indigo-50 rounded-lg">
                      <p className="text-lg font-semibold text-indigo-600">{projectionStatus.total_clusters}</p>
                      <p className="text-xs text-gray-500">Clusters</p>
                    </div>
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <p className="text-lg font-semibold text-gray-600">{projectionStatus.noise_count.toLocaleString()}</p>
                      <p className="text-xs text-gray-500">Noise</p>
                    </div>
                  </div>
                )}

                {projectionStatus?.computed_at && (
                  <p className="text-xs text-gray-500 mb-4">
                    Last computed: {formatRelativeTime(projectionStatus.computed_at)}
                  </p>
                )}

                <Collapsible open={showVisualizationOptions} onOpenChange={setShowVisualizationOptions}>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleComputeProjections}
                      disabled={computeProjectionsMutation.isPending || createK8sJobMutation.isPending || embeddedCount === 0}
                      className="flex-1"
                      variant={projectionStatus ? 'outline' : 'default'}
                    >
                      <IconWrapper icon={FiRefreshCw} className={`h-4 w-4 mr-2 ${(computeProjectionsMutation.isPending || createK8sJobMutation.isPending) ? 'animate-spin' : ''}`} />
                      {(computeProjectionsMutation.isPending || createK8sJobMutation.isPending)
                        ? 'Starting...'
                        : projectionStatus
                          ? 'Recompute'
                          : 'Compute Projections'
                      }
                    </Button>
                    {projectionStatus && (
                      <Button
                        onClick={handleRelabelClusters}
                        disabled={relabelClustersMutation.isPending}
                        variant="outline"
                      >
                        <IconWrapper icon={FiTag} className={`h-4 w-4 mr-2 ${relabelClustersMutation.isPending ? 'animate-spin' : ''}`} />
                        {relabelClustersMutation.isPending ? 'Labeling...' : 'Relabel'}
                      </Button>
                    )}
                    <CollapsibleTrigger asChild>
                      <Button variant="outline" size="icon">
                        <IconWrapper icon={showVisualizationOptions ? FiChevronUp : FiChevronDown} className="h-4 w-4" />
                      </Button>
                    </CollapsibleTrigger>
                  </div>
                  <CollapsibleContent className="mt-4 space-y-3">
                    <p className="text-xs text-gray-500">
                      Advanced parameters for UMAP dimensionality reduction and HDBSCAN clustering.
                    </p>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs text-gray-500">Min Cluster Size</label>
                        <Input
                          type="number"
                          min="5"
                          max="500"
                          value={vizMinClusterSize}
                          onChange={(e) => setVizMinClusterSize(e.target.value)}
                        />
                        <p className="text-xs text-gray-400 mt-1">
                          Minimum decisions to form a cluster. E.g., 100 means groups smaller than 100 become "noise".
                          <br />
                          <span className="text-gray-500">Lower → more clusters • Higher → fewer, larger clusters</span>
                        </p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">n_neighbors</label>
                        <Input
                          type="number"
                          min="5"
                          max="50"
                          value={vizNNeighbors}
                          onChange={(e) => setVizNNeighbors(e.target.value)}
                        />
                        <p className="text-xs text-gray-400 mt-1">
                          How much context to consider when placing each point.
                          <br />
                          <span className="text-gray-500">Lower → fine-grained local patterns • Higher → big-picture structure</span>
                        </p>
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">min_dist</label>
                        <Input
                          type="number"
                          min="0"
                          max="1"
                          step="0.05"
                          value={vizMinDist}
                          onChange={(e) => setVizMinDist(e.target.value)}
                        />
                        <p className="text-xs text-gray-400 mt-1">
                          How tightly points can pack together.
                          <br />
                          <span className="text-gray-500">Lower → tight clusters • Higher → spread out evenly</span>
                        </p>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                {/* Jobs Panel - shows K8s job history in staging/beta */}
                {isKubernetes && (
                  <Collapsible open={showJobsPanel} onOpenChange={setShowJobsPanel} className="mt-4">
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-gray-500 w-full justify-start">
                        <IconWrapper icon={showJobsPanel ? FiChevronUp : FiChevronDown} className="h-4 w-4 mr-1" />
                        {showJobsPanel ? 'Hide' : 'Show'} job history ({providerLabel})
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-3 pt-3 border-t border-gray-200">
                      <FOSProjectionJobsPanel limit={10} />
                    </CollapsibleContent>
                  </Collapsible>
                )}

                {embeddedCount === 0 && (
                  <p className="mt-3 text-xs text-amber-600">
                    Process decisions first to enable visualization.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Processing Log Feed */}
            <FOSProcessingLogFeed
              defaultExpanded={false}
              maxHeight="300px"
              maxEntries={100}
            />

            <FOSAdminIssuesCard
              issuesCount={issuesCount}
              stuckCount={stuckCount}
              failedCount={failedCount}
              stuckDecisions={stuckDecisions}
              failedDecisions={failedDecisions}
              requeueStuckPending={requeueStuckMutation.isPending}
              reprocessAllPending={reprocessAllMutation.isPending}
              resumeDecisionPending={resumeDecisionMutation.isPending}
              reprocessSinglePending={reprocessSingleMutation.isPending}
              onRequeueStuck={handleRequeueStuck}
              onReprocessAllFailed={handleReprocessAllFailed}
              onResumeDecision={handleResumeDecision}
              onProcessSingle={handleProcessSingle}
            />
          </TabsContent>

          <TabsContent value="browse">
            <FOSAdminBrowseTab
              searchQuery={searchQuery}
              processingStatusFilter={processingStatusFilter}
              decisionStatusFilter={decisionStatusFilter}
              industrySectorFilter={industrySectorFilter}
              sortBy={sortBy}
              sortOrder={sortOrder}
              hasActiveFilters={hasActiveFilters}
              totalBrowseItems={totalBrowseItems}
              browsePage={browsePage}
              totalBrowsePages={totalBrowsePages}
              decisionsData={decisionsData}
              decisionsLoading={decisionsLoading}
              reprocessSinglePending={reprocessSingleMutation.isPending}
              onSearchQueryChange={setSearchQuery}
              onProcessingStatusFilterChange={(value) => { setProcessingStatusFilter(value === 'all' ? '' : value); setBrowsePage(1); }}
              onDecisionStatusFilterChange={(value) => { setDecisionStatusFilter(value === 'all' ? '' : value); setBrowsePage(1); }}
              onIndustrySectorFilterChange={(value) => { setIndustrySectorFilter(value === 'all' ? '' : value); setBrowsePage(1); }}
              onSortByChange={setSortBy}
              onSortOrderChange={setSortOrder}
              onClearFilters={handleClearFilters}
              onBrowsePageChange={setBrowsePage}
              onSelectDecision={setSelectedDecisionId}
              onProcessSingle={handleProcessSingle}
            />
          </TabsContent>
        </Tabs>
      </div>

      <FOSDecisionDetailDialog
        selectedDecisionId={selectedDecisionId}
        decisionDetail={decisionDetail}
        detailLoading={detailLoading}
        reprocessSinglePending={reprocessSingleMutation.isPending}
        onOpenChange={(open) => !open && setSelectedDecisionId(null)}
        onProcessSingle={handleProcessSingle}
      />
    </div>
  );
};

export default FOSAdminDashboard;
